<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <div class="table-page-search-wrapper">
        <a-form layout="inline">
          <a-row :gutter="48">
            <a-col :md="8" :sm="24">
              <a-form-item :label="$t('views.cuttingTool.search.name')" prop="name">
                <a-input v-model="queryParam.name" :placeholder="$t('views.cuttingTool.search.namePlaceholder')" allow-clear/>
              </a-form-item>
            </a-col>
            <a-col :md="8" :sm="24">
              <span class="table-page-search-submitButtons">
                <a-button type="primary" @click="handleQuery"><a-icon type="search" />{{ $t('views.cuttingTool.btn.query') }}</a-button>
                <a-button style="margin-left: 8px" @click="resetQuery"><a-icon type="redo" />{{ $t('views.cuttingTool.btn.reset') }}</a-button>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>
      <div class="table-operations">
        <a-button type="primary" @click="$refs.createForm.handleAdd()" v-hasPermi="['user:cuttingTool:add']">
          <a-icon type="plus" />{{ $t('views.cuttingTool.btn.add') }}
        </a-button>
        <a-button type="primary" :disabled="single" @click="$refs.createForm.handleUpdate(undefined, ids)" v-hasPermi="['user:cuttingTool:edit']">
          <a-icon type="edit" />{{ $t('views.cuttingTool.btn.edit') }}
        </a-button>
        <a-button type="danger" :disabled="multiple" @click="handleDelete" v-hasPermi="['user:cuttingTool:remove']">
          <a-icon type="delete" />{{ $t('views.cuttingTool.btn.delete') }}
        </a-button>
        <table-setting
          :style="{float: 'right'}"
          :table-size.sync="tableSize"
          v-model="columns"
          :refresh-loading="loading"
          @refresh="getList" />
      </div>
      <create-form
        ref="createForm"
        @ok="getList"
      />
      <a-table
        :loading="loading"
        :size="tableSize"
        rowKey="id"
        :columns="columns"
        :data-source="list"
        :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
        :pagination="false"
        :bordered="tableBordered"
        :scroll="{ x: 'max-content' }"
      >
        <!-- 表格排序列已注释
        <span slot="sort" slot-scope="text, record">
          <a-input-number
            :value="record.sort"
            :min="0"
            style="width: 100px"
            @change="(value) => handleSortChange(record, value, 'sort')"
          />
        </span>
        -->
        <span slot="status" slot-scope="text, record">
          <a-switch
            :checked="record.status == 1 || record.status == '1'"
            :disabled="!canEdit"
            @change="(checked) => handleStatusChange(record, checked)"
          />
        </span>
        <span slot="createTime" slot-scope="text, record">
          {{ parseTime(record.createTime) }}
        </span>
        <span slot="operation" slot-scope="text, record">
          <a-divider type="vertical" v-hasPermi="['user:cuttingTool:edit']" />
          <a @click="$refs.createForm.handleUpdate(record, undefined)" v-hasPermi="['user:cuttingTool:edit']">
            <a-icon type="edit" />{{ $t('views.cuttingTool.btn.edit') }}
          </a>
          <a-divider type="vertical" v-hasPermi="['user:cuttingTool:remove']" />
          <a @click="handleDelete(record)" v-hasPermi="['user:cuttingTool:remove']">
            <a-icon type="delete" />{{ $t('views.cuttingTool.btn.delete') }}
          </a>
        </span>
      </a-table>
      <a-pagination
        class="ant-table-pagination"
        show-size-changer
        show-quick-jumper
        :current="queryParam.pageNum"
        :total="total"
        :page-size="queryParam.pageSize"
        :showTotal="paginationShowTotal"
        @showSizeChange="onShowSizeChange"
        @change="changeSize"
      />
    </a-card>
  </page-header-wrapper>
</template>

<script>
import { pageCuttingTool, delCuttingTool, updateCuttingToolSort, updateCuttingToolStatus } from '@/api/user/cuttingTool'
import CreateForm from './modules/CreateForm'
import { tableMixin } from '@/store/table-mixin'
import auth from '@/plugins/auth'

export default {
  name: 'CuttingTool',
  components: {
    CreateForm
  },
  mixins: [tableMixin],
  data () {
    return {
      list: [],
      selectedRowKeys: [],
      selectedRows: [],
      single: true,
      multiple: true,
      ids: [],
      loading: false,
      total: 0,
      queryParam: {
        name: null,
        pageNum: 1,
        pageSize: 10
      },
      columns: []
    }
  },
  computed: {
    /** 与 update-status 接口一致：user:cuttingTool:edit */
    canEdit () {
      return auth.hasPermi('user:cuttingTool:edit')
    }
  },
  created () {
    this.buildColumns()
    this.getList()
  },
  watch: {
    '$i18n.locale' () {
      this.buildColumns()
    }
  },
  methods: {
    buildColumns () {
      const t = k => this.$t(k)
      this.columns = [
        { title: t('views.cuttingTool.column.id'), dataIndex: 'id', ellipsis: true, align: 'center' },
        { title: t('views.cuttingTool.column.name'), dataIndex: 'name', ellipsis: true, align: 'center' },
        { title: t('views.cuttingTool.column.bagWidth'), dataIndex: 'bagWidth', ellipsis: true, align: 'center' },
        { title: t('views.cuttingTool.column.toolingFee'), dataIndex: 'toolingFee', ellipsis: true, align: 'center' },
        { title: t('views.cuttingTool.column.comparisonSymbols'), dataIndex: 'comparisonSymbols', ellipsis: true, align: 'center' },
        // { title: t('views.cuttingTool.column.sort'), dataIndex: 'sort', scopedSlots: { customRender: 'sort' }, ellipsis: true, align: 'center' },
        { title: t('views.cuttingTool.column.status'), dataIndex: 'status', scopedSlots: { customRender: 'status' }, ellipsis: true, align: 'center' },
        { title: t('views.cuttingTool.column.createTime'), dataIndex: 'createTime', scopedSlots: { customRender: 'createTime' }, ellipsis: true, align: 'center' },
        { title: t('views.cuttingTool.column.operation'), dataIndex: 'operation', width: 200, fixed: 'right', scopedSlots: { customRender: 'operation' }, align: 'center' }
      ]
    },
    paginationShowTotal (total) {
      return this.$t('views.cuttingTool.pagination.total', { total })
    },
    getList () {
      this.loading = true
     pageCuttingTool(this.queryParam).then(response => {
        this.list = response.data.records
        this.total = response.data.total
        this.loading = false
      })
    },
    handleQuery () {
      this.queryParam.pageNum = 1
      this.getList()
    },
    resetQuery () {
      this.queryParam = {
        name: undefined,
        pageNum: 1,
        pageSize: 10
      }
      this.handleQuery()
    },
    onShowSizeChange (current, pageSize) {
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    changeSize (current, pageSize) {
      this.queryParam.pageNum = current
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    onSelectChange (selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys
      this.selectedRows = selectedRows
      this.ids = this.selectedRows.map(item => item.id)
      this.single = selectedRowKeys.length !== 1
      this.multiple = !selectedRowKeys.length
    },
    handleDelete (row) {
      var that = this
      const ids = row.id || this.ids
      if(row.id){
        this.ids.push(row.id)
      }
      this.$confirm({
        title: this.$t('views.cuttingTool.confirm.deleteTitle'),
        content: this.$t('views.cuttingTool.confirm.deleteContent', { ids: ids }),
        onOk () {
          let data = []
          that.ids.map(id => data.push({"id":id}))
          return delCuttingTool(data)
            .then(() => {
              that.onSelectChange([], [])
              that.getList()
              that.$message.success(that.$t('views.cuttingTool.msg.deleteSuccess'), 3)
          })
        },
        onCancel () {
          that.ids = []
        }
      })
    },
    handleExport () {
      var that = this
      this.$confirm({
        title: this.$t('views.cuttingTool.confirm.exportTitle'),
        content: this.$t('views.cuttingTool.confirm.exportContent'),
        onOk () {
          that.download('user/cutting-tool/export', {
            ...that.queryParam
          }, that.$t('views.cuttingTool.export.filename', { time: new Date().getTime() }))
        },
        onCancel () {}
      })
    },
    handleStatusChange (record, checked) {
      if (!this.canEdit) {
        return
      }
      const statusValue = checked ? 1 : 0
      updateCuttingToolStatus({ id: record.id, status: statusValue }).then(() => {
        record.status = statusValue
        this.$message.success(this.$t('views.cuttingTool.msg.statusOk'))
        this.getList()
      }).catch(() => {
        this.$message.error(this.$t('views.cuttingTool.msg.statusFail'))
      })
    },
    handleSortChange (record, value, fieldName) {
      if (!this.canEdit) {
        return
      }
      if (value === null || value === undefined) {
        return
      }
      updateCuttingToolSort({ id: record.id, sort: value }).then(() => {
        record[fieldName] = value
        this.$message.success(this.$t('views.cuttingTool.msg.sortOk'))
        this.getList()
      }).catch(() => {
        this.$message.error(this.$t('views.cuttingTool.msg.sortFail'))
      })
    }
  }
}
</script>
